/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { TabProps, TabsProps } from '../../types';
import './Tabs.less';

// ---------------------------------------------------------------------------
// Context — shares selected value + setter between Tabs and Tab
// ---------------------------------------------------------------------------
interface TabsContextValue {
  value: string;
  onChange: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextValue>({
  value: '',
  onChange: () => {},
});

// ---------------------------------------------------------------------------
// Tab — individual tab button
// ---------------------------------------------------------------------------
const Tab: React.FC<TabProps> = ({ value, disabled = false, className, children }) => {
  const { value: selectedValue, onChange } = React.useContext(TabsContext);
  const isSelected = selectedValue === value;

  return (
    <button
      role="tab"
      type="button"
      aria-selected={isSelected}
      disabled={disabled}
      className={cx('tabs__tab', { 'tabs__tab--active': isSelected, 'tabs__tab--disabled': disabled }, className)}
      onClick={() => onChange(value)}>
      {children}
    </button>
  );
};

Tab.displayName = 'Tabs.Tab';

// ---------------------------------------------------------------------------
// Tabs — container / tablist
// ---------------------------------------------------------------------------
const TabsImpl: React.FC<TabsProps> = ({ value, defaultValue = '', onChange, className, children }) => {
  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const currentValue = isControlled ? value! : internalValue;

  const handleChange = React.useCallback(
    (next: string) => {
      if (!isControlled) setInternalValue(next);
      onChange?.(next);
    },
    [isControlled, onChange]
  );

  return (
    <TabsContext.Provider value={{ value: currentValue, onChange: handleChange }}>
      <div role="tablist" className={cx('tabs', className)}>
        {children}
      </div>
    </TabsContext.Provider>
  );
};

TabsImpl.displayName = 'Tabs';

type TabsComponent = React.FC<TabsProps> & { Tab: React.FC<TabProps> };

const Tabs = TabsImpl as TabsComponent;
Tabs.Tab = Tab;

export default Tabs;
